/**
 * Das Gerüst der Web-App: Seitenleiste, Kopfzeile, scrollender Inhalt.
 *
 * **Die Maße stammen aus `ui_kits/dashboard/`** — `Sidebar.jsx`, `TopBar.jsx`
 * und `components/navigation/NavItem.jsx` — und sind von dort übernommen, nicht
 * neu erfunden. Sie sehen willkürlich aus (7px, 13.5, 18px 20px 14px), sind es
 * aber nicht: Sie sind aufeinander abgestimmt, und wer einen Wert auf die
 * nächste runde Zahl zieht, verschiebt das Verhältnis zu allen anderen.
 *
 * Die Seitenleiste hat vier Zonen, von oben nach unten: Marke, `sidebarTop`
 * (im Kit der Anlagen-Umschalter), Navigation, `navFooter` (das Konto). Die
 * mittleren beiden dürfen fehlen; die Navigation dehnt sich, damit die Fußzeile
 * unten steht und nicht unter dem letzten Eintrag klebt.
 *
 * **Und sie lässt sich einklappen** — auf die 64 Pixel breite *Schiene*
 * (`layout.sidebarRailWidth`). Eingeklappt bleibt die Navigation
 * **vollständig**, nur ohne Beschriftung: Der eingeklappte Zustand ist eine
 * Sicht auf dieselbe Leiste und kein zweites, kürzeres Menü. Was wegfällt, ist,
 * was ohne Text nichts aussagt — Anlagenkopf, Abschnittsüberschrift, die Zeilen
 * der Kontozeile, die Rechtstexte.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { breakpoints, fontWeight, layout } from "../tokens.js";
import { IconButton } from "./icon-button.js";
import { IconChevronRight } from "./icons.js";
import { useNarrow } from "./use-narrow.js";

/**
 * Ob die Seitenleiste gerade als Schiene steht.
 *
 * **Als Kontext und nicht als Eigenschaft an jedem Einzelteil.** Betroffen sind
 * `NavItem`, `NavSection`, `SidebarUser` und in der Web-App außerdem das
 * Kontomenü und die Rechtslinks — den Zustand durch fünf Aufrufstellen zu
 * reichen hieße, ihn an fünf Stellen vergessen zu können, und die vergessene
 * fiele erst auf, wenn jemand einklappt.
 *
 * **Er gilt nur der breiten Ansicht.** Als Schublade (`breakpoints.shell`) ist
 * die Leiste immer ausgeklappt: Sie liegt dort ohnehin über dem Inhalt und
 * nimmt ihm keine Breite weg, und eine Schiene, die man erst aufziehen muss,
 * wäre ein zweiter Griff für dieselbe Bewegung.
 */
const SidebarCollapsedContext = createContext(false);

/** Für Teile der Seitenleiste, die außerhalb dieses Pakets liegen. */
export function useSidebarCollapsed(): boolean {
  return useContext(SidebarCollapsedContext);
}

export interface AppShellProps {
  /** Oben in der Seitenleiste — `Wordmark`, mit oder ohne Logo. */
  readonly brand: ReactNode;
  /**
   * Die Marke für die eingeklappte Schiene — im Kit das Logo **ohne** den
   * Schriftzug.
   *
   * **Sie kommt von außen und wird nicht aus `brand` gekürzt.** Was von einer
   * Marke auf 64 Pixeln übrig bleibt, entscheidet die Marke und nicht das
   * Gerüst; `brand` ist ein `ReactNode`, und ihn zu zerlegen hieße, auf seinen
   * inneren Aufbau zu wetten. Fehlt sie, bleibt die Stelle eingeklappt leer.
   */
  readonly brandMark?: ReactNode;
  /**
   * Ob die Seitenleiste als Schiene steht. **Ohne `onToggleCollapse` wirkungslos**
   * — eine eingeklappte Leiste, die sich nicht aufklappen lässt, ist eine
   * Sackgasse.
   *
   * **Der Zustand gehört der Anwendung**, nicht dem Gerüst: Er soll ein
   * Neuladen überleben, und wo er das tut, weiß nur sie
   * (`apps/web/src/lib/sidebar-rail.ts`).
   */
  readonly collapsed?: boolean;
  /** Klappt ein und aus. Fehlt sie, gibt es den Schalter nicht. */
  readonly onToggleCollapse?: () => void;
  /**
   * Zwischen Marke und Navigation. Im Design-System steht hier der Umschalter
   * für die Anlage: Er gehört über die Navigation, weil er festlegt, worauf
   * sich alles darunter bezieht.
   */
  readonly sidebarTop?: ReactNode;
  /** Die Navigationseinträge, üblicherweise mehrere `NavItem` und `NavSection`. */
  readonly nav: ReactNode;
  /** Unten in der Seitenleiste — üblicherweise `SidebarUser`. */
  readonly navFooter?: ReactNode;
  readonly title: ReactNode;
  /**
   * Der Weg eine Ebene zurück, **links vom Titel** — im Kopf des Rahmens, wo ein
   * geöffnetes Gerät oder ein geöffneter Zähler den Titel stellt (Kit:
   * `TopBar.jsx`).
   *
   * **Als Adresse und nicht als Rückruf allein.** Die Liste, aus der man kommt,
   * hat eine — ein `<button>` davor wäre weder zu kopieren noch im neuen Tab zu
   * öffnen (HM-038). `onNavigate` fängt den gewöhnlichen Klick ab; Strg-, Cmd-
   * und Mittelklick gehen an den Browser.
   *
   * **`label` benennt das Ziel** („Geräteverwaltung") und nicht das Wort
   * „zurück": Daraus wird „Zurück zu {label}" — als `aria-label` und als
   * Sprechblase. So weiß man vorher, wo man landet.
   */
  readonly back?: { readonly href: string; readonly label: string; readonly onNavigate: () => void };
  /** Direkt neben dem Titel — im Kit die Marke „Live". */
  readonly titleBadge?: ReactNode;
  /** Die Zeile unter dem Titel — im Kit die Anlage und was die Seite zeigt. */
  readonly subtitle?: ReactNode;
  /** Rechts in der Kopfzeile — Zeitraum, Primäraktion, Abmelden. */
  readonly headerRight?: ReactNode;
  /**
   * **Wechselt dieser Wert, schließt sich die Schublade.**
   *
   * Auf einem schmalen Fenster liegt die Navigation über dem Inhalt; wer darin
   * etwas auswählt, will das Ergebnis sehen und nicht weiter die Liste. Ein
   * Klick-Zuhörer auf der Leiste täte es nicht: Der Umschalter für die Anlage
   * öffnet dort ein eigenes Menü, und die Schublade fiele beim Aufklappen zu.
   *
   * Was hier hineingehört, ist die **Adresse** — sie ist genau dann eine
   * andere, wenn sich die Ansicht wirklich geändert hat.
   */
  readonly navKey?: string;
  readonly children: ReactNode;
}

export function AppShell({
  brand,
  brandMark,
  collapsed = false,
  onToggleCollapse,
  sidebarTop,
  nav,
  navFooter,
  title,
  back,
  titleBadge,
  subtitle,
  headerRight,
  navKey,
  children,
}: AppShellProps) {
  /**
   * **Unterhalb von `shell` gibt es keine Seitenleiste mehr, sondern eine
   * Schublade.** Bei 248 Pixeln fester Leiste blieben von einem 380 Pixel
   * breiten Fenster 132 für den Inhalt — das ist keine schmale Ansicht mehr,
   * sondern eine unbenutzbare.
   *
   * Diese eine Entscheidung lässt sich nicht ins Stylesheet legen: Die Leiste
   * ist einmal ein Geschwisterelement im Raster und einmal eine Fläche über
   * allem, mit Grund davor und Fokus darin. Beides gleichzeitig zu zeichnen und
   * eines zu verstecken hieße, jeden Navigationseintrag doppelt in die Seite zu
   * schreiben.
   */
  const narrow = useNarrow(breakpoints.shell);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  /* Ein Bereichswechsel schließt sie. Siehe `navKey`. */
  useEffect(() => {
    setDrawerOpen(false);
  }, [navKey]);

  /**
   * Und ein wieder breit gewordenes Fenster ebenso. Ohne das bliebe der Zustand
   * stehen: Wer die Schublade öffnet, das Fenster aufzieht und später wieder
   * schmal macht, fände sie offen vor, ohne sie geöffnet zu haben.
   */
  useEffect(() => {
    if (!narrow) setDrawerOpen(false);
  }, [narrow]);

  /**
   * Escape schließt, und der Fokus wandert mit — hinein beim Öffnen, zurück auf
   * den Menüknopf beim Schließen. Ohne das steht der Fokus nach dem Öffnen
   * weiterhin hinter einer Fläche, die man gerade darübergelegt hat.
   *
   * **Zurückgesetzt wird im Effekt und nicht im Klick-Zuhörer.** Der Rest der
   * Seite ist offen, solange die Schublade steht; ein `focus()` mitten im
   * Ereignis träfe den Knopf, während er noch `inert` ist, und verpuffte. Der
   * Effekt läuft nach dem Zeichnen — dann ist er wieder erreichbar.
   *
   * `wasOpen` unterscheidet dabei „gerade geschlossen" von „war nie offen":
   * Ohne die Merkzeile risse der erste Aufbau der Seite den Fokus an sich.
   */
  useEffect(() => {
    if (drawerOpen) {
      wasOpen.current = true;
      drawer.current?.focus();
      const onKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") setDrawerOpen(false);
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }

    if (wasOpen.current) {
      wasOpen.current = false;
      menuButton.current?.focus();
    }
    return undefined;
  }, [drawerOpen]);

  /**
   * **Eingeklappt gilt nur breit und nur mit Schalter.** Als Schublade nimmt
   * die Leiste dem Inhalt keine Breite weg, und ohne `onToggleCollapse` käme
   * niemand wieder heraus — ein Zustand, aus dem es keinen Weg zurück gibt,
   * wird gar nicht erst eingenommen.
   */
  const railed = collapsed && !narrow && onToggleCollapse !== undefined;

  const collapseButton =
    onToggleCollapse === undefined || narrow ? null : (
      <button
        type="button"
        onClick={onToggleCollapse}
        aria-label={railed ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
        aria-expanded={!railed}
        aria-controls="hm-sidebar-nav"
        title={railed ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: railed ? 28 : 26,
          height: railed ? 28 : 26,
          flexShrink: 0,
          /* Ausgeklappt sitzt er oben rechts in der Ecke, auf Höhe des
             Schriftzugs — so bleibt der Chip der Dachmarke darunter frei und
             die Marke behält die volle Breite. Eingeklappt steht er mittig in
             seiner eigenen Zeile: Neben einem 34-Pixel-Logo auf 64 Pixeln
             Breite bliebe für ihn nichts mehr. */
          position: railed ? undefined : "absolute",
          top: railed ? undefined : 12,
          right: railed ? undefined : 8,
          border: "none",
          borderRadius: "var(--radius-sm)",
          background: "transparent",
          cursor: "pointer",
          color: "var(--text-faint)",
        }}
      >
        {/* Der Pfeil zeigt, wohin die Kante geht — eingeklappt nach rechts,
            ausgeklappt nach links. Ein eigenes Symbol dafür kennt der Satz
            nicht, und eines zu erfinden hieße, dem System eine Marke
            hinzuzufügen, die dort nicht beschlossen ist. */}
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            transform: railed ? undefined : "rotate(180deg)",
          }}
        >
          <IconChevronRight size={17} />
        </span>
      </button>
    );

  const sidebar = (
    <SidebarCollapsedContext.Provider value={railed}>
      <aside
        style={{
          display: "flex",
          flexDirection: "column",
          /* Als Schublade steht sie nicht mehr in einer Rasterspalte und braucht
             ihre Breite selbst. Im Raster gibt sie die Spalte vor. */
          width: narrow ? layout.sidebarWidth : undefined,
          maxWidth: "100%",
          height: "100%",
          minHeight: 0,
          background: "var(--surface-card)",
          borderRight: "1px solid var(--border-subtle)",
          /* **Nicht mehr `overflowY` an der Leiste selbst**, sondern an der
             Navigation darin (siehe dort): Sonst scrollen Marke, Umschalter und
             Kontozeile mit, und die Kontozeile ist genau das, was unten stehen
             bleiben soll. */
          overflow: "hidden",
        }}
      >
        {/* Kit: padding '20px 20px 16px', gap 11 — keine feste Höhe. */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: railed ? "center" : "flex-start",
            gap: 11,
            padding: railed ? "18px 0 14px" : "20px 20px 16px",
            flexShrink: 0,
          }}
        >
          {/* Eingeklappt nur das Zeichen — der Schriftzug passt auf 64 Pixel
              nicht, und abgeschnitten wäre er schlechter als keiner. Fehlt
              `brandMark`, bleibt die Zeile leer statt eine Marke zu erfinden. */}
          {railed ? brandMark : brand}
          {!railed && collapseButton}
        </div>

        {/* Eingeklappt bekommt der Schalter seine eigene Zeile. */}
        {railed && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexShrink: 0,
              marginBottom: 6,
            }}
          >
            {collapseButton}
          </div>
        )}

        {/* Kit: margin '0 12px'. Eingeklappt fällt der Anlagenkopf weg: Name und
            Anschrift sind der ganze Inhalt, und ein Symbol allein sagt nicht,
            welche Anlage gilt. Schmal steht er in der Kopfzeile statt in der
            Schublade (siehe unten). */}
        {sidebarTop !== undefined && !railed && !narrow && (
          <div style={{ padding: "0 12px", flexShrink: 0 }}>{sidebarTop}</div>
        )}

        {/* Kit: padding '10px 12px 4px', gap 3, flex 1.

            **Hier scrollt die Leiste, und nur hier.** `minHeight: 0` ist die
            Bedingung dafür — ein Flex-Kind schrumpft sonst nicht unter seinen
            Inhalt, und `overflowY` bliebe wirkungslos. Der weiche Abschluss
            (`maskImage`) sagt, dass unten noch etwas kommt: Eine hart
            abgeschnittene Zeile sieht aus wie das Ende der Liste. */}
        <nav
          id="hm-sidebar-nav"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: railed ? "10px 8px 4px" : "10px 12px 4px",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            maskImage: "linear-gradient(to bottom, #000 calc(100% - 18px), transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, #000 calc(100% - 18px), transparent)",
          }}
        >
          {nav}
        </nav>

        {/* Kit: padding '14px 18px', borderTop. */}
        {navFooter !== undefined && (
          <div
            style={{
              flexShrink: 0,
              padding: railed ? "14px 0" : "14px 18px",
              borderTop: "1px solid var(--border-subtle)",
            }}
          >
            {navFooter}
          </div>
        )}
      </aside>
    </SidebarCollapsedContext.Provider>
  );

  /* `minWidth: 0` — ohne das sprengt eine breite Tabelle das Raster, statt in
     ihrem eigenen Bereich zu scrollen. `minHeight: 0` tut dasselbe für die Höhe
     und ist die Bedingung dafür, dass `main` unten scrollt. */
  const content = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        /* `flex: 1` gilt nur schmal — dort steht der Bereich unter der
           Markenzeile in einer Flex-Spalte und dürfte nicht die volle
           Fensterhöhe für sich nehmen. Im Raster daneben ist die Angabe ohne
           Wirkung, und `height` gibt dort die Höhe. */
        flex: 1,
        height: "100%",
      }}
      /**
       * **`inert`, solange die Schublade offen steht.** Der Grund davor hält
       * die Maus auf; die Tabulatortaste hält er nicht auf, und ohne diese
       * Zeile wanderte der Fokus hinter die Fläche, während der Bildschirm
       * etwas anderes zeigt. Ein Fokusfänger wie im `<dialog>` wäre der zweite
       * Weg — `inert` sagt dasselbe in einem Wort und meldet es nebenbei jeder
       * Sprachausgabe.
       */
      inert={narrow && drawerOpen}
    >
      {/* Kit: padding '16px 28px', gap '12px 14px', flexWrap. */}
      <header
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "12px 14px",
          flexShrink: 0,
          padding: narrow ? "12px 14px" : "16px 28px",
          background: "var(--surface-card)",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        {/**
         * **Zurück gehört neben den Titel, den es verlässt** — so setzt es
         * `TopBar.jsx` im Design-System-Projekt, und die Form ist der
         * `IconButton` daraus: `ghost`, `sm`, ein um 180° gedrehter Chevron.
         *
         * Im Kopf **des Rahmens** und nicht im Inhalt darunter: Dort wanderte
         * der Ausgang beim Scrollen einer langen Detailseite weg.
         *
         * **Das Ziel steht in `label`** und wird zu `aria-label` **und**
         * `title` — die Sprechblase nennt es also der Maus, die Sprachausgabe
         * liest es vor. Ein sichtbarer Text daneben wäre ein zweiter auf
         * derselben Zeile und ließe offen, welcher von beiden die Seite
         * benennt.
         */}
        {back !== undefined && <BackLink back={back} />}

        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1
              style={{
                /* Kit: 19 statt 22, sobald die Marke über den Titel rückt. Eine
                   22-Pixel-Überschrift unter einer 17-Pixel-Wortmarke sähe aus,
                   als wäre die Marke die Unterzeile. */
                fontSize: narrow ? 19 : 22,
                fontWeight: fontWeight.bold,
                letterSpacing: "var(--tracking-tight)",
              }}
            >
              {title}
            </h1>
            {titleBadge}
          </div>
          {subtitle !== undefined && (
            <div
              style={{
                marginTop: 2,
                fontSize: 13,
                color: "var(--text-muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {headerRight !== undefined && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              /* Kit: schmal rückt die rechte Gruppe unter den Titel und nimmt
                 die ganze Zeile. Rechtsbündig gedrängt bliebe für sie neben
                 einem Titel kaum mehr Platz als für ein Wort. */
              marginLeft: narrow ? 0 : "auto",
              width: narrow ? "100%" : "auto",
            }}
          >
            {headerRight}
          </div>
        )}
      </header>

      {/* Kit: padding '24px 28px 40px', schmal '16px 14px 32px'. `minHeight: 0`
          — ein Flex-Kind schrumpft sonst nicht unter seinen Inhalt und
          `overflowY` bliebe wirkungslos. */}
      <main
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: narrow ? "16px 14px 32px" : "24px 28px 40px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 18,
            maxWidth: layout.containerMax,
            margin: "0 auto",
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );

  if (narrow) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
          background: "var(--surface-page)",
        }}
      >
        {/* Kit: padding '10px 14px', gap 10 — die Zeile, die es nur schmal
            gibt. Sie trägt die Marke, weil die Seitenleiste sie gerade nicht
            zeigt, und den einzigen Weg zurück zu ihr.

            **Und den Anlagen-Umschalter, neben dem Zeichen.** Er sagt, worauf
            sich die ganze Seite bezieht — in der geschlossenen Schublade stand
            das nirgends, und wer die Anlage wechseln wollte, musste erst das
            Menü öffnen. Mit ihm bleibt von der Marke nur `brandMark`: Schriftzug
            und Anlagenname haben nebeneinander auf einem Telefon keinen Platz.
            In der Schublade steht er dann nicht noch einmal. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
            padding: "10px 14px",
            background: "var(--surface-card)",
            borderBottom: "1px solid var(--border-subtle)",
          }}
          inert={drawerOpen}
        >
          <button
            ref={menuButton}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menü öffnen"
            aria-expanded={drawerOpen}
            aria-controls="hm-sidebar-drawer"
            style={{
              display: "inline-flex",
              flexDirection: "column",
              gap: 4,
              padding: 9,
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-sm)",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            {/* Drei Striche, wie im Kit gezeichnet und nicht als Symbol aus dem
                Satz: Der Symbolsatz des Design-Systems kennt keines dafür, und
                eines zu erfinden hieße, dem System eine Marke hinzuzufügen, die
                dort nicht beschlossen ist. */}
            {[0, 1, 2].map((bar) => (
              <span
                key={bar}
                aria-hidden="true"
                style={{ width: 16, height: 2, borderRadius: 2, background: "var(--text-strong)" }}
              />
            ))}
          </button>
          {sidebarTop !== undefined ? (
            <>
              {brandMark ?? brand}
              <div style={{ flex: 1, minWidth: 0 }}>{sidebarTop}</div>
            </>
          ) : (
            brand
          )}
        </div>

        {drawerOpen && (
          <div
            /* Der Grund. Ein Klick darauf schließt — dieselbe Erwartung wie bei
               jedem Menü, das sich über die Seite legt. */
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              background: "var(--overlay-scrim)",
            }}
          >
            <div
              id="hm-sidebar-drawer"
              ref={drawer}
              tabIndex={-1}
              /* Der Klick in die Leiste darf nicht bis zum Grund durchfallen —
                 sonst schlösse jeder Griff an den Umschalter die Schublade. */
              onClick={(event) => event.stopPropagation()}
              style={{
                height: "100%",
                width: layout.sidebarWidth,
                maxWidth: "85%",
                boxShadow: "var(--shadow-lg)",
                outline: "none",
              }}
            >
              {sidebar}
            </div>
          </div>
        )}

        {content}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        /* Die Spalte folgt dem Zustand — und mit `transition` schiebt sich die
           Kante, statt zu springen. Kit: `transition: 'width .16s ease'`. */
        gridTemplateColumns: `${railed ? layout.sidebarRailWidth : layout.sidebarWidth}px 1fr`,
        transition: "grid-template-columns .16s ease",
        height: "100%",
        /**
         * **Diese beiden Zeilen halten die Seitenleiste stehen.**
         *
         * Ein Grid-Element hat `min-height: auto` und schrumpft damit nicht
         * unter seinen Inhalt. Ohne `minHeight: 0` wächst die rechte Spalte über
         * die Fensterhöhe hinaus, das Raster wächst mit — `height` ist keine
         * Obergrenze —, und dann scrollt das **Fenster** statt `main`. Die
         * Seitenleiste wandert dabei mit nach oben hinaus.
         *
         * `overflow: hidden` ist der Riegel dahinter: Was trotzdem zu groß wird,
         * bekommt seinen eigenen Scrollbereich, statt das Gerüst aufzuziehen.
         * Dieselbe Kombination steht deshalb an jedem verschachtelten Kind.
         */
        minHeight: 0,
        overflow: "hidden",
        background: "var(--surface-page)",
      }}
    >
      {sidebar}
      {content}
    </div>
  );
}

/**
 * Der Weg eine Ebene zurück, links vom Titel — die Form aus `TopBar.jsx` im
 * Design-System-Projekt.
 *
 * **Der `IconButton` des Design-Systems und kein eigener Knopf.** Er stand hier
 * einmal als handgezeichnetes `<a>` mit eigenen Maßen; das ist genau der Weg,
 * auf dem eine Oberfläche zwei Sorten desselben Knopfes bekommt. Gestaltung
 * kommt aus den Komponenten, nicht aus einem `style`-Objekt daneben.
 *
 * **Mit `href`, also als Link.** Das Ziel ist eine Liste mit eigener Adresse;
 * ein `<button>` davor nähme dem Strg-Klick den neuen Tab und ließe sich nicht
 * kopieren (HM-038). Dafür hat `IconButton` seit HM-075 dieselbe Weiche wie
 * `NavItem`. Der Rückruf gilt nur dem gewöhnlichen Klick — mit Zusatztaste oder
 * mittlerer Maustaste macht der Browser, was er soll.
 *
 * **Der Name des Ziels steht in `label`**, nicht das Wort „zurück": „Zurück zu
 * Geräteverwaltung" sagt vorher, wo man landet, und `IconButton` setzt ihn als
 * `aria-label` **und** `title`.
 */
function BackLink({
  back,
}: {
  back: { href: string; label: string; onNavigate: () => void };
}) {
  return (
    <IconButton
      href={back.href}
      label={`Zurück zu ${back.label}`}
      variant="ghost"
      size="sm"
      style={{ marginLeft: -4 }}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        back.onNavigate();
      }}
    >
      {/* Kit: `<span style={{ transform: 'rotate(180deg)' }}><IcChevron size={18} /></span>` —
          ein eigenes Zeichen für „zurück" kennt der Satz nicht, und eines zu
          erfinden hieße, dem System eine Marke hinzuzufügen. */}
      <span aria-hidden="true" style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
        <IconChevronRight size={18} />
      </span>
    </IconButton>
  );
}

export interface NavItemProps {
  /** Ziel als Adresse. Fehlt sie, wird der Eintrag zur Schaltfläche. */
  readonly href?: string;
  readonly onClick?: () => void;
  readonly children: ReactNode;
  readonly active?: boolean;
  readonly icon?: ReactNode;
  /** Rechts im Eintrag — eine Anzahl, als Pille. Nur zeigen, was gezählt wurde. */
  readonly badge?: ReactNode;
  /**
   * Der Name des Eintrags als reine Zeichenkette — für die **eingeklappte**
   * Leiste, in der nur das Symbol steht und der Name in `title` und
   * `aria-label` wandert.
   *
   * Fehlt er, wird er aus `children` genommen, sofern das ein Text ist. Steht
   * dort etwas anderes, hat der Eintrag eingeklappt **keinen** Namen — und ein
   * Symbol ohne Namen ist für eine Sprachausgabe eine leere Schaltfläche. Wer
   * eine Beschriftung aus mehreren Teilen setzt, gibt `label` deshalb dazu.
   */
  readonly label?: string;
}

/**
 * Ein Eintrag der Seitenleiste. Maße aus `components/navigation/NavItem.jsx`.
 *
 * Der aktive Eintrag trägt die Markenfarbe auf ruhiger Hinterlegung
 * (`--hm-green-50`). Das ist der Ort, an dem die Marke hingehört — und einer der
 * Gründe, warum sie in keiner Datendarstellung vorkommen darf: Wer Grün einmal
 * hier gelernt hat, soll es nicht später an einer Kurve wiederfinden (ADR 0013).
 *
 * **Das Symbol trägt eine eigene Farbe**, kräftiger als der Text: aktiv das
 * Marken-Grün, sonst gedämpft. Ohne das verschwimmt die Leiste zu einer Spalte
 * gleich grauer Zeilen.
 *
 * **Die Schriftstärke bleibt `medium`, auch aktiv.** Im Kit ist das so, und es
 * ist richtig: Ein Wechsel der Strichstärke ändert die Textbreite, und beim
 * Umschalten zwischen zwei Einträgen zuckt dann die ganze Leiste.
 *
 * **Link oder Schaltfläche, je nach `href`.** Mit Adresse wird es ein `<a>`, das
 * sich in einem neuen Tab öffnen und kopieren lässt; ohne Adresse ein `<button>`.
 * Ein `<a href="#">`, das per JavaScript die Ansicht wechselt, wäre beides nicht
 * richtig: Es verspricht ein Ziel, das es nicht gibt. Solange kein Router da ist
 * (HM-010), wechseln die Bereiche über die Schaltfläche.
 */
export function NavItem({
  href,
  onClick,
  children,
  active = false,
  icon,
  badge,
  label,
}: NavItemProps) {
  const [hovered, setHovered] = useState(false);
  const collapsed = useSidebarCollapsed();

  const name = label ?? (typeof children === "string" ? children : undefined);

  /**
   * **Eingeklappt trägt der Name die Anzahl mit** — „Geräteverwaltung (3)".
   * Das Kit lässt die Pille in der Schiene ersatzlos weg; sie passt dort auch
   * nicht neben ein 40 Pixel großes Symbol. Eine Zahl, die beim Einklappen
   * verschwindet, wäre aber ein Verlust an Auskunft und kein Gewinn an Ruhe:
   * Sie steht deshalb dort, wo eingeklappt ohnehin der ganze Eintrag steht.
   */
  const collapsedName =
    name === undefined ? undefined : badge === undefined ? name : `${name} (${String(badge)})`;

  const style: CSSProperties = collapsed
    ? {
        /* Kit: 40×40, mittig in der Schiene, `alignSelf: center`. */
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        alignSelf: "center",
        border: "none",
        borderRadius: "var(--radius-md)",
        color: active ? "var(--hm-green-700)" : "var(--text-muted)",
        background: active
          ? "var(--hm-green-50)"
          : hovered
            ? "var(--surface-sunken)"
            : "transparent",
        textDecoration: "none",
        cursor: "pointer",
        transition: "background .15s ease, color .15s ease",
      }
    : {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "7px 10px",
    border: "none",
    borderRadius: "var(--radius-sm)",
    fontFamily: "var(--font-body)",
    fontSize: 13.5,
    fontWeight: fontWeight.medium,
    color: active ? "var(--hm-green-700)" : "var(--text-body)",
    background: active
      ? "var(--hm-green-50)"
      : hovered
        ? "var(--surface-sunken)"
        : "transparent",
    textAlign: "left",
    textDecoration: "none",
    cursor: "pointer",
    transition: "background .15s ease, color .15s ease",
  };

  /* Eingeklappt trägt das Symbol die Farbe des Eintrags selbst — eine zweite,
     kräftigere Symbolfarbe neben einem Text, den es nicht gibt, ordnete nichts
     mehr, sondern setzte nur einen Akzent. */
  const content = collapsed ? (
    icon
  ) : (
    <>
      {icon !== undefined && (
        <span
          style={{
            display: "inline-flex",
            width: 18,
            height: 18,
            flexShrink: 0,
            color: active ? "var(--hm-green-600)" : "var(--text-muted)",
          }}
        >
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {badge !== undefined && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontSize: 11.5,
            fontWeight: fontWeight.semibold,
            padding: "1px 7px",
            borderRadius: "var(--radius-pill)",
            background: active ? "var(--hm-green-500)" : "var(--surface-sunken)",
            color: active ? "var(--text-invert)" : "var(--text-muted)",
            flexShrink: 0,
          }}
        >
          {badge}
        </span>
      )}
    </>
  );

  const handlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    /* Ausgeklappt steht der Name daneben; ein `title`, der ihn wiederholt, wäre
       eine Sprechblase über jedem Eintrag der Leiste. */
    ...(collapsed ? { title: collapsedName, "aria-label": collapsedName } : {}),
  };

  if (href === undefined) {
    return (
      <button
        type="button"
        onClick={onClick}
        /* `aria-current="page"` gilt auch ohne Adresse: Es sagt „hier sind Sie",
           nicht „hierhin führt ein Link". */
        aria-current={active ? "page" : undefined}
        style={style}
        {...handlers}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      style={style}
      {...handlers}
    >
      {content}
    </a>
  );
}

/**
 * Zwischenüberschrift in der Seitenleiste — „System". Maße aus `Sidebar.jsx`.
 *
 * Trennt zwei Gruppen von Einträgen: was man täglich ansieht und was man
 * einrichtet. Die Linie darüber macht den Schnitt sichtbar, auch wenn die
 * Überschrift überlesen wird.
 */
export function NavSection({ children }: { children: ReactNode }) {
  const collapsed = useSidebarCollapsed();

  return (
    <>
      {/* Kit: eine eigene 1px-Fläche mit margin '10px 12px' — sie reicht
          absichtlich nicht bis an den Rand der Leiste. */}
      <div
        style={{
          height: 1,
          background: "var(--border-subtle)",
          margin: collapsed ? "10px 6px" : "10px 12px",
        }}
      />
      {/* **Eingeklappt bleibt der Schnitt, die Überschrift geht.** Die Linie
          trennt weiterhin, was man täglich ansieht, von dem, was man einrichtet;
          das Wort „Verwaltung" auf 64 Pixeln wäre entweder unleserlich klein
          oder abgeschnitten. */}
      {!collapsed && (
        <div
          style={{
            padding: "4px 12px 6px",
            fontSize: 11,
            fontWeight: fontWeight.bold,
            letterSpacing: "var(--tracking-caps)",
            textTransform: "uppercase",
            color: "var(--text-faint)",
          }}
        >
          {children}
        </div>
      )}
    </>
  );
}

export interface SidebarUserProps {
  /** Die obere, kräftige Zeile — Name oder E-Mail-Adresse. */
  readonly name: ReactNode;
  /** Die untere, ruhige Zeile — die Rolle. */
  readonly detail?: ReactNode;
  readonly icon?: ReactNode;
  /**
   * Rechts daneben — im Kit ein Schalter. Zusammen mit `onClick` darf hier
   * **nichts Bedienbares** stehen, sondern nur ein Zeichen wie `IconUnfold`:
   * Eine Schaltfläche in einer Schaltfläche ist ungültiges HTML, und der Klick
   * träfe beide.
   */
  readonly action?: ReactNode;
  /**
   * Macht die Zeile zur Schaltfläche, die ein Menü öffnet. Ohne sie bleibt sie
   * reine Anzeige.
   */
  readonly onClick?: () => void;
  /** Ob das zugehörige Menü offen steht. Nur zusammen mit `onClick` sinnvoll. */
  readonly expanded?: boolean;
  /**
   * Der Name als reine Zeichenkette — für die **eingeklappte** Leiste, in der
   * von der Zeile nur das Bild übrig bleibt. Fehlt er, wird `name` genommen,
   * sofern das ein Text ist; dieselbe Regel wie bei `NavItem.label` und aus
   * demselben Grund.
   */
  readonly label?: string;
}

/**
 * Die Kontozeile am Fuß der Seitenleiste. Maße aus `Sidebar.jsx`.
 *
 * Aus dem Kit übernommen, ohne den Werbekasten darüber: „HausMeter Plus" wirbt
 * dort für Funktionen, die es nicht gibt, und für ein Preismodell, das nicht
 * entschieden ist (`docs/adr/OPEN-QUESTIONS.md`).
 *
 * **Mit `onClick` wird aus der Zeile ein `<button>`**, und damit der einzige
 * Weg zu allem, was nicht zur Anlage gehört: Die Navigation darüber führt
 * ausschließlich Anlagen-Bereiche, das Konto ist die Ausnahme am Fuß. Ein
 * `<div onClick>` täte es nicht — es ist mit der Tastatur nicht erreichbar und
 * meldet einer Sprachausgabe nicht, dass sich hier etwas öffnet.
 *
 * Was dann in `action` steht, muss reine Anzeige sein — siehe dort.
 */
export function SidebarUser({
  name,
  detail,
  icon,
  action,
  onClick,
  expanded = false,
  label,
}: SidebarUserProps) {
  const collapsed = useSidebarCollapsed();
  const accessibleName = label ?? (typeof name === "string" ? name : undefined);

  const content = (
    <>
      {icon !== undefined && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: "50%",
            /**
             * **`overflow: hidden` gehört zum Radius dazu**, sobald hier ein
             * Bild statt eines gezeichneten Symbols steht: Ein `border-radius`
             * rundet den Rahmen, beschneidet aber nichts darin. Ohne das ragt
             * ein Profilbild als Quadrat über den runden Rand hinaus — und der
             * Rand verschwindet dahinter, statt das Bild zu formen.
             */
            overflow: "hidden",
            background: "var(--hm-green-100)",
            color: "var(--hm-green-700)",
            /* Für Initialen statt eines Symbols — Maße aus `Sidebar.jsx`. Auf
               ein Symbol wirkt beides nicht. */
            fontFamily: "var(--font-display)",
            fontSize: 13,
            fontWeight: fontWeight.extrabold,
          }}
        >
          {icon}
        </span>
      )}
      {/* Eingeklappt bleibt das Bild. Name, Rolle und Aufklapp-Zeichen fallen
          weg — sie sind Text, und Text ist genau das, was auf der Schiene
          keinen Platz hat. Der Name wandert in `title` und `aria-label`. */}
      {!collapsed && (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: fontWeight.semibold,
                color: "var(--text-strong)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </div>
            {detail !== undefined && (
              <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{detail}</div>
            )}
          </div>
          {action !== undefined && <div style={{ flexShrink: 0 }}>{action}</div>}
        </>
      )}
    </>
  );

  const row: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: collapsed ? "center" : undefined,
    gap: 10,
    width: "100%",
  };

  if (onClick === undefined)
    return (
      <div style={row} title={collapsed ? accessibleName : undefined}>
        {content}
      </div>
    );

  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={expanded}
      title={collapsed ? accessibleName : undefined}
      aria-label={collapsed ? accessibleName : undefined}
      style={{
        ...row,
        /* Der Rahmen erscheint erst beim Zeigen bzw. wenn das Menü offen steht —
           eine dauerhaft umrandete Fläche am Fuß der Leiste zöge Aufmerksamkeit
           auf sich, die dem Inhalt darüber gehört.

           Eingeklappt entfallen die negativen Ränder: Sie holen ausgeklappt den
           Rahmen über das Polster der Fußzeile hinaus, auf 64 Pixeln Breite
           schöben sie ihn über die Kante der Leiste. */
        margin: collapsed ? undefined : "-6px -8px",
        padding: collapsed ? 6 : "6px 8px",
        border: "1px solid transparent",
        borderColor: expanded ? "var(--border-subtle)" : "transparent",
        borderRadius: "var(--radius-md)",
        background: expanded ? "var(--surface-sunken)" : "transparent",
        textAlign: "left",
        cursor: "pointer",
        transition: "background .15s ease, border-color .15s ease",
      }}
    >
      {content}
    </button>
  );
}

/**
 * Die Marke: Logo und Schriftzug nebeneinander, wie in `Sidebar.jsx`.
 *
 * **Kein nachgezeichnetes Logo.** Das echte ist eine Bilddatei im
 * Design-System-Projekt (`assets/logo.png`); sie neu zu zeichnen ist
 * ausdrücklich untersagt. Das Paket kennt die Datei nicht — die Anwendung
 * reicht sie als `logo` herein.
 *
 * `logo` wird dem Schriftzug **vorangestellt**, nicht an seiner Stelle
 * eingesetzt: Im Kit stehen beide nebeneinander, das Bild links in 34 px mit
 * 9 px Radius.
 *
 * **`endorsement` setzt die Dachmarke als Chip darunter** — „by Christians
 * Technikshop" mit Kontur und der Glühbirne aus dem Original-Logo
 * (`assets/ctshop-mark.png`, ein Ausschnitt, nicht nachgezeichnet; die
 * Anwendung reicht sie als `endorsementMark` herein). Unter 16 px Schriftzug
 * entfällt sie: Der Chip wäre dann nicht mehr lesbar.
 */
export function Wordmark({
  logo,
  size = 19,
  logoSize,
  tone = "default",
  endorsement = false,
  endorsementMark,
}: {
  logo?: ReactNode;
  /** Schriftgröße des Schriftzugs. Kit: 19 in der Seitenleiste. */
  readonly size?: number;
  /**
   * Kantenlänge des Logos. Kit: 34 ohne Dachmarke. **Mit** Dachmarke wird sie
   * gerechnet, wenn sie fehlt — siehe unten.
   */
  readonly logoSize?: number;
  /**
   * `invert` für einen farbigen oder dunklen Grund — der Schriftzug steht dann
   * ganz in Weiß.
   *
   * **Das grüne „Meter" fällt dabei weg, und das ist keine Nachlässigkeit.**
   * Auf `--hm-green-600` säße die Markenfarbe auf sich selbst: Der Kontrast
   * zwischen `--hm-green-500` und dem Grund liegt bei 1,4:1, die Silbe wäre
   * nicht mehr zu lesen. Eine hellere Marken-Abstufung dafür zu wählen hieße,
   * eine vierte Farbe zu erfinden, die es im System nicht gibt.
   */
  readonly tone?: "default" | "invert";
  /** Die Dachmarke als Chip unter dem Schriftzug (Kit: Variante F1). */
  readonly endorsement?: boolean;
  /** Das Bild im Chip — die Glühbirne aus dem Logo von Christians Technikshop. */
  readonly endorsementMark?: ReactNode;
}) {
  const invert = tone === "invert";
  const chipFont = Math.max(10, Math.round(size * 0.385));
  const withChip = endorsement && size >= 16;
  /**
   * **Mit Chip schließt das Logo oben mit dem Schriftzug und unten mit dem Chip
   * ab.** `logo.png` hat rund 9 % transparenten Rand; das Bild wird deshalb so
   * weit vergrößert, dass die *sichtbare* Marke die Höhe von Schriftzug, Abstand
   * und Chip hat, und der Rand mit negativem Außenabstand wieder eingezogen.
   * Ein Radius entfällt dann — er schnitte in den transparenten Rand und nicht
   * in die Marke.
   */
  const gap = Math.round(size * 0.2);
  const chipHeight = Math.round(chipFont * 2);
  const visible = size + gap + chipHeight;
  const edge = logoSize ?? (withChip ? Math.round(visible / 0.82) : 34);
  const trim = withChip && logoSize === undefined ? Math.round((edge - visible) / 2) : 0;

  const word = (
    <span
      style={{
        fontFamily: "var(--font-display)",
        fontSize: size,
        fontWeight: fontWeight.extrabold,
        letterSpacing: "var(--tracking-tight)",
        lineHeight: withChip ? 1 : undefined,
        color: invert ? "var(--text-invert)" : "var(--text-strong)",
      }}
    >
      Haus
      <span style={{ color: invert ? "inherit" : "var(--hm-green-500)" }}>Meter</span>
    </span>
  );

  const chip = withChip && (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: Math.round(chipFont * 0.3),
        alignSelf: "flex-start",
        height: chipHeight,
        padding: `0 ${Math.round(chipFont * 0.8)}px 0 ${Math.round(chipFont * 0.3)}px`,
        borderRadius: 999,
        border: `1px solid ${invert ? "rgba(255,255,255,.55)" : "var(--border-default)"}`,
        background: invert ? "transparent" : "var(--surface-card)",
        fontFamily: "var(--font-body)",
        fontSize: chipFont,
        fontWeight: fontWeight.semibold,
        lineHeight: 1,
        whiteSpace: "nowrap",
        color: invert ? "var(--text-invert)" : "var(--text-body)",
      }}
    >
      {endorsementMark !== undefined && (
        <span
          aria-hidden="true"
          style={{ display: "inline-flex", height: Math.round(chipHeight * 0.8) }}
        >
          {endorsementMark}
        </span>
      )}
      <span
        style={{
          fontWeight: fontWeight.medium,
          color: invert ? "inherit" : "var(--text-faint)",
          opacity: invert ? 0.85 : 1,
        }}
      >
        by
      </span>
      Christians Technikshop
    </span>
  );

  return (
    <>
      {logo !== undefined && (
        <span
          style={{
            display: "inline-flex",
            width: edge,
            height: edge,
            flexShrink: 0,
            margin: trim ? `${-trim}px ${-trim + 2}px ${-trim}px ${-trim}px` : undefined,
            /* Kit: 9 bei 34 px. Als Verhältnis, damit eine andere Größe nicht
               plötzlich eckiger oder runder aussieht. */
            borderRadius: trim ? 0 : Math.round(edge * 0.26),
            overflow: "hidden",
          }}
        >
          {logo}
        </span>
      )}
      {chip ? (
        <span style={{ display: "inline-flex", flexDirection: "column", gap, minWidth: 0 }}>
          {word}
          {chip}
        </span>
      ) : (
        word
      )}
    </>
  );
}
